import { Component, OnInit, ViewChild, OnDestroy, Input, SimpleChanges } from '@angular/core';
import { Chart } from 'chart.js';
import { colors } from 'src/app/colors';
import { createDailyUsageChartConfig } from '../../reports-config';

@Component({
    selector: 'app-daily-usage-chart',
    templateUrl: './daily-usage-chart.component.html',
    styleUrls: ['./daily-usage-chart.component.css'],
    standalone: false
})
export class DailyUsageChartComponent implements OnDestroy {
  @ViewChild('dailyUsageCanvas') dailyUsageCanvas: any;
  chart: Chart | null = null;

  @Input('usageData') usageData: any = null;
  @Input('loading') loading: boolean = false;

  // Pagination properties
  allRows: any[] = [];
  visibleRows: any[] = [];
  currentWeekIndex: number = 0;
  totalWeeks: number = 0;
  weekRangeLabel: string = '';

  ngAfterViewInit() {
    if (this.usageData && !this.loading) {
      this.processData(this.usageData);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['loading'] && !this.loading) || (changes['usageData'] && this.usageData)) {
      setTimeout(() => {
        this.processData(this.usageData);
      }, 0);
    }
  }

  processData(apiResponse: any) {
    if (!apiResponse || !apiResponse.rows) {
      this.allRows = [];
      this.totalWeeks = 0;
      this.visibleRows = [];
      this.updateVisibleWeek();
      return;
    }

    this.allRows = apiResponse.rows;
    // Calculate how many 7-day chunks we have
    this.totalWeeks = Math.ceil(this.allRows.length / 7);

    // Default to the first week (earliest data in the range)
    this.currentWeekIndex = 0;
    this.updateVisibleWeek();
  }

  updateVisibleWeek() {
    const start = this.currentWeekIndex * 7;
    const end = start + 7;
    this.visibleRows = this.allRows.slice(start, end);

    // Set Header Range Label (e.g., "01 Feb - 07 Feb")
    if (this.visibleRows.length > 0) {
      const firstDay = this.visibleRows[0].label;
      const lastDay = this.visibleRows[this.visibleRows.length - 1].label;
      this.weekRangeLabel = firstDay === lastDay ? firstDay : `${firstDay} - ${lastDay}`;
    }

    this.initializeChart();
  }

  changeWeek(delta: number) {
    const newIndex = this.currentWeekIndex + delta;
    if (newIndex >= 0 && newIndex < this.totalWeeks) {
      this.currentWeekIndex = newIndex;
      this.updateVisibleWeek();
    }
  }

  initializeChart() {
    if (!this.dailyUsageCanvas) return;

    const ctx = this.dailyUsageCanvas.nativeElement.getContext('2d');
    const labels = this.visibleRows.map(row => row.label);
    const appData = this.visibleRows.map(row => parseFloat((row.app_seconds / 3600).toFixed(2)));
    const webData = this.visibleRows.map(row => parseFloat((row.web_seconds / 3600).toFixed(2)));

    const maxVal = Math.max(...appData, ...webData, 5);
    const yAxisMax = Math.ceil(maxVal + (maxVal * 0.15));

    const appGradient = ctx.createLinearGradient(0, 0, 0, 400);
    
    appGradient.addColorStop(0, colors.dailyUsageChart.appGradient[0]);
    appGradient.addColorStop(1, colors.dailyUsageChart.appGradient[1]);

    const webGradient = ctx.createLinearGradient(0, 0, 0, 400);
    webGradient.addColorStop(0, colors.dailyUsageChart.webGradient[0]);
    webGradient.addColorStop(1, colors.dailyUsageChart.webGradient[1]);

    const chartConfig: any = createDailyUsageChartConfig(labels, appData, webData, appGradient, webGradient, yAxisMax);

    if (this.chart) {
      this.chart.destroy();
    }
    this.chart = new Chart(ctx, chartConfig);
  }

  ngOnDestroy() {
    if (this.chart) this.chart.destroy();
  }
}
